/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-cycle
import {
  AssistantFile,
  AssistantForm,
  AssistantStatus,
  AssistantTable,
  AssistantTableFilters,
} from '@src/types/assistant';

export type Loading = 'idle' | 'pending' | 'success' | 'failure' | 'fulfill';

/**
 * How long a file confirmed by its POST stays in the list when a later GET
 * comes without it (ENG-2062): the provider's listing can lag behind a large
 * file for a while; past this the list is believed as is.
 */
export const RECENT_UPLOAD_TTL_MS = 120_000;

/** A file the upload endpoint confirmed (so it has an id), with the moment it did. */
export type RecentUpload = {
  file: AssistantFile & { id: string };
  uploadedAt: number;
};

export type AssistantFilesEntry = {
  storeId?: string;
  files: AssistantFile[];
  status: Loading;
  /** Uploads confirmed by their POST, kept while a GET may still miss them (ENG-2062). */
  recentUploads?: RecentUpload[];
};

export type AssistantState = {
  assistantsTable: {
    data: AssistantTable[];
    filters: AssistantTableFilters;
    status: Loading;
    paging: {
      hasMore: boolean;
    };
  };

  assistantsConfig?: AssistantForm;
  assistantFilesQueue?: File[];
  assistantFilesQueueStoreId?: string;
  assistantFiles: {
    [assistantId: string]: AssistantFilesEntry;
  };
  assistantFileDeleteError?: string;
  /** Last failed upload: the files that did not reach the server and why (ENG-2027). */
  assistantFileUploadError?: { message?: string; filenames: string[] };

  assistantRequestStatus: Loading;
};

type AssistantReducer<P = void> = (
  state: AssistantState,
  action: PayloadAction<P>
) => void;

const initialState: AssistantState = {
  assistantsTable: {
    data: [],
    filters: {
      nameLike: '',
      onlyActive: false,
    },
    status: 'idle',
    paging: {
      hasMore: true,
    },
  },
  assistantsConfig: undefined,
  assistantFiles: {},
  assistantFilesQueue: [],
  assistantFilesQueueStoreId: undefined,
  assistantRequestStatus: 'idle',
  // assistantFilesStatus: 'idle',
};

const fetchAssistantsTable: AssistantReducer<
  AssistantTableFilters & { limit: number; offset?: number }
> = (state, action) => {
  if (
    action.payload.nameLike !== state.assistantsTable.filters.nameLike ||
    action.payload.onlyActive !== state.assistantsTable.filters.onlyActive
  ) {
    state.assistantsTable.data = [];
    state.assistantsTable.paging.hasMore = true;
  }

  state.assistantsTable.filters = {
    nameLike: action.payload.nameLike,
    onlyActive: action.payload.onlyActive,
  };

  action.payload.offset = state.assistantsTable.data.length;
  state.assistantsTable.status = 'pending';
};

const setFetchAssistantsTableRequest: AssistantReducer<{
  data?: AssistantTable[];
  hasMore?: boolean;
  status: Loading;
}> = (state, { payload: { data, hasMore, status } }) => {
  state.assistantsTable.status = status;
  if (data) state.assistantsTable.data.push(...data);
  if (hasMore !== undefined) state.assistantsTable.paging.hasMore = hasMore;
};

const updateAssistantStatus: AssistantReducer<{
  assistantId: string;
  assistantStatus: AssistantStatus;
}> = state => {
  state.assistantRequestStatus = 'pending';
};

const updateAssistantStatusRequest: AssistantReducer<{
  assistantStatus?: AssistantStatus;
  status: Loading;
}> = (state, { payload: { status, assistantStatus } }) => {
  state.assistantRequestStatus = status;
  if (typeof assistantStatus !== 'undefined' && state.assistantsConfig)
    state.assistantsConfig.status = assistantStatus;
};

const fetchAssistantStore: AssistantReducer<string> = () => {};

const setFetchAssistantStoreRequest: AssistantReducer<{
  status: Loading;
  assistantId?: string;
  assistantStoreId?: string;
}> = (state, { payload: { assistantStoreId, assistantId } }) => {
  if (assistantStoreId && assistantId)
    state.assistantFiles[assistantId] = {
      ...(state.assistantFiles[assistantId] || {}),
      storeId: assistantStoreId,
    };
};

const fetchAssistantFiles: AssistantReducer<{
  assistantId: string;
  assistantStoreId: string;
}> = (state, { payload: { assistantId, assistantStoreId } }) => {
  // state.assistantFilesStatus = 'pending';
  if (assistantId) {
    // Keep the rest of the entry (recentUploads above all): rebuilding it here
    // dropped the uploads the POST had just confirmed, so the list that
    // followed could not keep them (ENG-2062, found by the e2e journey)
    state.assistantFiles[assistantId] = {
      ...state.assistantFiles[assistantId],
      status: 'pending',
      storeId: assistantStoreId,
      files: state.assistantFiles[assistantId]?.files || [],
    };
  }
};

// A POST answer is a row only when it is an object with the file id; the
// endpoint may answer a bare string on an unexpected empty result.
const isConfirmedFile = (
  value: unknown
): value is AssistantFile & { id: string } =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as AssistantFile).id === 'string';

const setFetchAssistantFilesRequest: AssistantReducer<{
  status: Loading;
  assistantId: string;
  assistantFiles?: AssistantFile[];
  /** When the list was received; lets the reducer expire recent uploads without a clock. */
  receivedAt?: number;
}> = (
  state,
  { payload: { assistantId, assistantFiles, status, receivedAt } }
) => {
  const current: Partial<AssistantFilesEntry> =
    state.assistantFiles[assistantId] || {};
  let files = assistantFiles ?? current.files ?? [];
  let { recentUploads } = current;

  if (assistantFiles) {
    // A list that arrives without a file the POST already confirmed is the
    // provider lagging behind (ENG-2062): the file stays visible, in progress,
    // until a later list includes it or the TTL passes.
    const listed = new Set(assistantFiles.map(f => f.id));
    recentUploads = (current.recentUploads ?? []).filter(
      recent =>
        !listed.has(recent.file.id) &&
        receivedAt !== undefined &&
        receivedAt - recent.uploadedAt < RECENT_UPLOAD_TTL_MS
    );
    files = [...assistantFiles, ...recentUploads.map(recent => recent.file)];
  }

  state.assistantFiles[assistantId] = {
    ...current,
    files,
    status,
    recentUploads,
  };
};

const uploadAssistantFile: AssistantReducer<{
  assistantStoreId?: string;
  assistantFiles: File[];
}> = (state, { payload }) => {
  state.assistantFilesQueue = [
    ...(state.assistantFilesQueue || []),
    ...payload.assistantFiles,
  ];
  state.assistantFilesQueueStoreId = payload.assistantStoreId;

  if (payload?.assistantStoreId) {
    // eslint-disable-next-line no-restricted-syntax
    for (const [assistantId, assistantFiles] of Object.entries(
      state.assistantFiles
    )) {
      if (assistantFiles.storeId === payload.assistantStoreId)
        state.assistantFiles[assistantId].status = 'pending';
    }
  }
  // state.assistantFilesStatus = 'pending';
};

const setUploadAssistantFileRequest: AssistantReducer<{
  assistantStoreId?: string;
  status: Loading;
  /** On failure: the server's message, when it sent one. */
  errorMessage?: string;
  /** On failure: the files that were not uploaded (the failed one and the rest of the batch). */
  failedFilenames?: string[];
  /** On success: the rows the upload endpoint answered, one per file (ENG-2062). */
  uploadedFiles?: AssistantFile[];
  /** On success: when the upload finished. */
  uploadedAt?: number;
}> = (
  state,
  {
    payload: {
      assistantStoreId,
      status,
      errorMessage,
      failedFilenames,
      uploadedFiles,
      uploadedAt,
    },
  }
) => {
  const confirmed =
    status === 'success' ? (uploadedFiles ?? []).filter(isConfirmedFile) : [];

  if (assistantStoreId) {
    // eslint-disable-next-line no-restricted-syntax
    for (const [assistantId, assistantFiles] of Object.entries(
      state.assistantFiles
    )) {
      if (assistantFiles.storeId === assistantStoreId) {
        const entry = state.assistantFiles[assistantId];
        entry.status = status;
        if (confirmed.length) {
          // The POST already carries the row: painting it now means the list
          // does not depend on the provider having caught up (ENG-2062).
          // The store may be known before any list was fetched
          const existing = entry.files ?? [];
          const known = new Set(existing.map(f => f.id));
          entry.files = [
            ...existing,
            ...confirmed.filter(f => !known.has(f.id)),
          ];
          const confirmedIds = new Set(confirmed.map(f => f.id));
          entry.recentUploads = [
            ...(entry.recentUploads ?? []).filter(
              recent => !confirmedIds.has(recent.file.id)
            ),
            ...confirmed.map(file => ({ file, uploadedAt: uploadedAt ?? 0 })),
          ];
        }
      }
    }
  }

  // The queue is what the block paints as «Procesando»: it empties whether the
  // upload ended well or badly, or a failed file would spin for ever (ENG-2027).
  if (status === 'success' || status === 'failure') {
    state.assistantFilesQueue = [];
    state.assistantFilesQueueStoreId = undefined;
  }
  if (status === 'failure') {
    state.assistantFileUploadError = {
      message: errorMessage,
      filenames: failedFilenames ?? [],
    };
  }
  // state.assistantFilesStatus = status;
};

const clearAssistantFileUploadError: AssistantReducer = state => {
  state.assistantFileUploadError = undefined;
};

const deleteAssistantFile: AssistantReducer<{
  assistantId: string;
  assistantStoreId: string;
  assistantFileId: string;
}> = (state, { payload: { assistantId } }) => {
  // state.assistantFilesStatus = 'pending';
  if (assistantId) state.assistantFiles[assistantId].status = 'pending';
};

const setDeleteAssistantFileRequest: AssistantReducer<{
  status: Loading;
  assistantId: string;
  assistantFileId?: string;
  errorMessage?: string;
}> = (
  state,
  { payload: { status, assistantId, assistantFileId, errorMessage } }
) => {
  if (status === 'success' && state.assistantFiles[assistantId]) {
    const entry = state.assistantFiles[assistantId];
    entry.files = entry.files?.filter(({ id }) => id !== assistantFileId);
    // A file deleted within the TTL of its upload must not come back from the
    // recent uploads on the next list (ENG-2062, PR review)
    entry.recentUploads = entry.recentUploads?.filter(
      ({ file }) => file.id !== assistantFileId
    );
  } else if (status === 'failure' && errorMessage) {
    state.assistantFileDeleteError = errorMessage;
    state.assistantFiles[assistantId].status = 'idle';
  } else {
    state.assistantFiles[assistantId].status = status;
  }
};

const clearAssistantFileDeleteError: AssistantReducer = state => {
  state.assistantFileDeleteError = undefined;
};

const resetAssistantConfig: AssistantReducer = state => {
  state.assistantsConfig = undefined;
};

const assistantsSlice = createSlice({
  name: 'Assistants',
  initialState,
  reducers: {
    fetchAssistantsTable,
    setFetchAssistantsTableRequest,

    updateAssistantStatus,
    updateAssistantStatusRequest,
    resetAssistantConfig,

    fetchAssistantStore,
    setFetchAssistantStoreRequest,

    fetchAssistantFiles,
    setFetchAssistantFilesRequest,

    uploadAssistantFile,
    setUploadAssistantFileRequest,
    clearAssistantFileUploadError,

    deleteAssistantFile,
    setDeleteAssistantFileRequest,
    clearAssistantFileDeleteError,
  },
});

const { actions, reducer } = assistantsSlice;

export const assistantActions = actions;

export default reducer;
